import axios from 'axios';
import { settings } from '@/plugins/settings';

export interface ISubscriptionRefund {
  id: number;
  stripe_refund_id: string;
  type: 'full' | 'partial';
  amount: number;
  currency: string;
  stripe_status: string;
  canceled_subscription: boolean;
  created_at: string | null;
}

export interface ISubscriptionListItem {
  id: number;
  user?: any;
  service_provider?: any;
  plan?: any;
  status?: string;
  cancel_at_period_end?: boolean;
  cancellation_reason?: string | null;
  cancellation_reason_text?: string | null;
  parent_subscription_id?: number | null;
  root_subscription_id?: number | null;
  renewed_to_subscription_id?: number | null;
  stripe_subscription_id?: string;
  current_period_end?: string | null;
  canceled_at?: string | null;
  pause_collection_resumes_at?: string | null;
  pause_collection_behavior?: string | null;
  created_at?: string | null;
  orders_count?: number;
  refunds?: ISubscriptionRefund[];
  amount?: number | null;
  currency?: string | null;
}

export interface ISubscriptionPage {
  data: ISubscriptionListItem[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
}

const LIST_ENDPOINTS = [
  'subscriptions/',
];

function normalizeList(response: any): any[] {
  // ApiResponse wraps the body in { data: ... }
  // Paginated responses: { data: { data: [...], current_page, ... } }
  if (response && response.data && response.data.data && Array.isArray(response.data.data.data)) {
    return response.data.data.data;
  }
  if (response && response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response && Array.isArray(response.data)) {
    return response.data;
  }
  return [];
}

async function fetchPageFromCandidates(params: any = {}): Promise<ISubscriptionPage> {
  for (const endpoint of LIST_ENDPOINTS) {
    try {
      const response = await axios({
        url: settings.endpoint + endpoint,
        method: 'GET',
        params,
      });
      const envelope = response && response.data && response.data.data ? response.data.data : null;
      if (envelope && Array.isArray(envelope.data)) {
        return {
          data:         envelope.data,
          total:        envelope.total || envelope.data.length,
          current_page: envelope.current_page || 1,
          last_page:    envelope.last_page || 1,
          per_page:     envelope.per_page || envelope.data.length,
        };
      }
      // flat array fallback
      const flat = normalizeList(response);
      return { data: flat, total: flat.length, current_page: 1, last_page: 1, per_page: flat.length };
    } catch (_error) {
      // Try next candidate endpoint.
    }
  }
  return { data: [], total: 0, current_page: 1, last_page: 1, per_page: 15 };
}

async function fetchFromCandidates(params: any = {}): Promise<any[]> {
  const page = await fetchPageFromCandidates(params);
  return page.data;
}

export const subscriptionService = {
  fetchSubscriptions(params: any = {}): Promise<ISubscriptionListItem[]> {
    return fetchFromCandidates(params) as Promise<ISubscriptionListItem[]>;
  },

  fetchSubscriptionsRaw(params: any = {}): Promise<ISubscriptionPage> {
    return fetchPageFromCandidates(params);
  },

  fetchOrdersBySubscription(subscriptionId: number): Promise<any[]> {
    return axios({
      url: settings.endpoint + 'orders/',
      method: 'GET',
      params: {
        page: 0,
        serviceProviderSubscriptionId: subscriptionId,
      },
    }).then((response: any) => {
      if (response && response.data && Array.isArray(response.data.data)) return response.data.data;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    });
  },

  cancelSubscription(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/cancel`,
      method: 'POST',
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  retrySubscription(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/retry`,
      method: 'POST',
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  reactivateSubscription(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/reactivate`,
      method: 'POST',
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  renewSubscription(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/renew`,
      method: 'POST',
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  fetchRenewPreview(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/renew-preview`,
      method: 'GET',
    }).then((response: any) => {
      const envelope = response && response.data && response.data.data;
      return (envelope && envelope.data) ? envelope.data : envelope || null;
    });
  },

  pauseSubscription(subscriptionId: number, resumeAt?: string): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/pause`,
      method: 'POST',
      data: resumeAt ? { resume_at: resumeAt } : {},
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  resumeFromPauseSubscription(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/resume-pause`,
      method: 'POST',
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },

  fetchRefundPreview(subscriptionId: number): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/refund-preview`,
      method: 'GET',
    }).then((response: any) => {
      const envelope = response && response.data && response.data.data;
      return (envelope && envelope.data) ? envelope.data : envelope || null;
    });
  },

  refundSubscription(
    subscriptionId: number,
    params: { type: 'full' | 'partial'; amount?: number; cancel_after_refund?: boolean },
  ): Promise<any> {
    return axios({
      url: settings.endpoint + `subscriptions/${subscriptionId}/refund`,
      method: 'POST',
      data: params,
    }).then((response: any) => (response && response.data && response.data.data) || null);
  },
};
